import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import AgentLayout, { agentFetch } from './AgentLayout';

/**
 * AgentHistory — Page historique client/lead (Historique sprint, H2+H5).
 *
 * Affiche la timeline agrégée des événements liés au client (interviews,
 * matchings, dossiers, emails, interactions agent) + permet à l'agent
 * d'ajouter une note ou de saisir la disposition d'un appel.
 *
 * Backend :
 *   - GET  /api/agent/leads/:id/timeline      → events triés desc
 *   - POST /api/agent/leads/:id/interactions  → ajoute note/call_outcome
 *
 * Pattern UI : timeline verticale avec puces colorées par type d'event.
 * Modal d'ajout en overlay.
 */
export default function AgentHistory() {
  const { id: leadId } = useParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState(false);

  // Chargement timeline complète (mode normal) OU recherche full-text (mode search).
  // Debounce 300ms en mode search pour éviter le spam backend pendant la frappe.
  const load = useCallback((query) => {
    setLoading(true);
    setError(null);
    const isSearch = query && query.trim().length >= 2;
    const url = isSearch
      ? `/api/agent/leads/${leadId}/timeline/search?q=${encodeURIComponent(query.trim())}&limit=100`
      : `/api/agent/leads/${leadId}/timeline?limit=200`;
    agentFetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        setEvents(Array.isArray(data.events) ? data.events : []);
        setSearchMode(isSearch);
      })
      .catch(() => setError('Erreur de chargement de l\'historique'))
      .finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => { load(''); }, [load]);

  // Debounced search trigger
  useEffect(() => {
    const t = setTimeout(() => { load(searchQuery); }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, load]);

  const onInteractionAdded = () => { setModalOpen(false); load(searchQuery); };

  return (
    <AgentLayout>
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <Link to={`/agent/clients/${leadId}`} style={{ color: '#D4724A', fontSize: 13, textDecoration: 'none' }}>
              ← Retour à la fiche client
            </Link>
            <h1 style={{ fontFamily: 'Lora, serif', fontSize: 26, color: '#2D2D2D', margin: '8px 0 0' }}>
              Historique client
            </h1>
            <p style={{ fontSize: 13, color: '#8A8A8A', margin: '4px 0 0' }}>
              {searchMode
                ? `${events.length} résultat${events.length > 1 ? 's' : ''} pour « ${searchQuery} »`
                : `${events.length} événement${events.length > 1 ? 's' : ''} chronologique${events.length > 1 ? 's' : ''}`}
            </p>
          </div>
          <button onClick={() => setModalOpen(true)} style={btnPrimary}>+ Ajouter une note</button>
        </div>

        {/* H6 — barre de recherche full-text */}
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="🔍 Rechercher dans les notes, transcripts, emails… (min 2 caractères)"
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 10,
              border: '1.5px solid #D9D9D9', fontSize: 14,
              boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: '#8A8A8A', fontSize: 18, padding: '0 8px',
              }}
              aria-label="Effacer la recherche"
            >×</button>
          )}
        </div>

        {loading && <p style={{ color: '#8A8A8A' }}>Chargement…</p>}
        {error && (
          <div style={errorBoxStyle}>
            <span>{error}</span>
            <button onClick={load} style={btnSecondary}>Réessayer</button>
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <p style={{ color: '#8A8A8A', textAlign: 'center', padding: 40 }}>
            Aucun événement pour ce client. Ajoute une note pour démarrer l'historique.
          </p>
        )}

        {!loading && events.length > 0 && (
          <div style={{ position: 'relative', paddingLeft: 30 }}>
            {/* Ligne verticale gauche */}
            <div style={{ position: 'absolute', left: 8, top: 8, bottom: 8, width: 2, background: '#E0DDD8' }} />
            {events.map((ev, i) => <TimelineItem key={ev.id} event={ev} isLast={i === events.length - 1} />)}
          </div>
        )}
      </div>

      {modalOpen && (
        <AddInteractionModal
          leadId={leadId}
          onClose={() => setModalOpen(false)}
          onSuccess={onInteractionAdded}
        />
      )}
    </AgentLayout>
  );
}

// ---------- Timeline item ----------

function TimelineItem({ event }) {
  const { color, icon, label } = typeMeta(event.type);
  const date = new Date(event.at);
  const dateLabel = date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeLabel = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ position: 'relative', paddingBottom: 18 }}>
      <div style={{
        position: 'absolute', left: -27, top: 4,
        width: 18, height: 18, borderRadius: '50%',
        background: color, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700,
      }}>{icon}</div>
      <div style={{
        background: '#FDFCFA', borderRadius: 10,
        border: '1px solid #E0DDD8', padding: '10px 14px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#2D2D2D' }}>{event.title}</span>
          <span style={{ fontSize: 11, color: '#8A8A8A' }}>{dateLabel} · {timeLabel}</span>
        </div>
        {event.details && (
          <div style={{ fontSize: 13, color: '#5A5A5A', marginTop: 4, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {event.details}
          </div>
        )}
        <div style={{ fontSize: 11, color: '#8A8A8A', marginTop: 4 }}>
          {label}
          {event.by && <> · par <code style={{ background: '#F5F3F0', padding: '1px 5px', borderRadius: 3 }}>{event.by}</code></>}
        </div>
      </div>
    </div>
  );
}

function typeMeta(type) {
  switch (type) {
    case 'lead_created':            return { color: '#8A8A8A', icon: '+', label: 'Lead créé' };
    case 'lead_profile_updated':    return { color: '#7B61FF', icon: '✎', label: 'Profil modifié' };
    case 'interview_started':       return { color: '#2980B9', icon: 'I', label: 'Interview' };
    case 'interview_completed':     return { color: '#2980B9', icon: '✓', label: 'Interview terminée' };
    case 'matching_calculated':     return { color: '#7B61FF', icon: 'M', label: 'Matching d\'aides' };
    case 'dossier_created':         return { color: '#D4724A', icon: 'D', label: 'Dossier' };
    case 'dossier_step_completed':  return { color: '#3D8B5E', icon: '✓', label: 'Étape dossier' };
    case 'email_sent':              return { color: '#3D8B5E', icon: '✉', label: 'Email' };
    case 'interaction_note':        return { color: '#F0A500', icon: 'N', label: 'Note agent' };
    case 'interaction_call':        return { color: '#D4724A', icon: '☎', label: 'Appel' };
    default:                        return { color: '#8A8A8A', icon: '?', label: type };
  }
}

// ---------- Modal Add interaction ----------

function AddInteractionModal({ leadId, onClose, onSuccess }) {
  const [type, setType] = useState('call_outcome');
  const [disposition, setDisposition] = useState('interesse');
  const [note, setNote] = useState('');
  const [dateRappel, setDateRappel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = { type, note };
      if (type === 'call_outcome') body.disposition = disposition;
      if (type === 'call_outcome' && disposition === 'rappel') {
        if (!dateRappel) {
          setError('Date de rappel requise');
          setSubmitting(false);
          return;
        }
        body.dateRappel = new Date(dateRappel).toISOString();
      }
      const res = await agentFetch(`/api/agent/leads/${leadId}/interactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const respBody = await res.json().catch(() => ({}));
        throw new Error(respBody.message || `Erreur ${res.status}`);
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: 24, maxWidth: 520, width: '100%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#2D2D2D' }}>Ajouter à l'historique</h3>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={fieldLabel}>Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <RadioPill checked={type === 'note'} onChange={() => setType('note')} label="Note libre" />
              <RadioPill checked={type === 'call_outcome'} onChange={() => setType('call_outcome')} label="Bilan d'appel" />
            </div>
          </div>

          {type === 'call_outcome' && (
            <div style={{ marginBottom: 14 }}>
              <label style={fieldLabel}>Disposition</label>
              <select value={disposition} onChange={(e) => setDisposition(e.target.value)} style={inputStyle}>
                <option value="interesse">Intéressé</option>
                <option value="rappel">À rappeler</option>
                <option value="refuse">Refus</option>
                <option value="injoignable">Injoignable</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          )}

          {type === 'call_outcome' && disposition === 'rappel' && (
            <div style={{ marginBottom: 14 }}>
              <label style={fieldLabel}>Date de rappel</label>
              <input
                type="datetime-local"
                required
                value={dateRappel}
                onChange={(e) => setDateRappel(e.target.value)}
                style={inputStyle}
                min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
              />
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={fieldLabel}>Note (max 2000 caractères)</label>
            <textarea
              required
              rows={5}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              placeholder="Ex: Client demande à être rappelé vendredi après-midi, intéressé par l'APA pour sa mère"
            />
            <div style={{ fontSize: 11, color: '#8A8A8A', textAlign: 'right', marginTop: 2 }}>
              {note.length} / 2000
            </div>
          </div>

          {error && (
            <div style={{ padding: 10, borderRadius: 8, background: '#FDECEA', color: '#933', fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSecondary} disabled={submitting}>Annuler</button>
            <button type="submit" disabled={submitting || !note.trim()} style={btnPrimary}>
              {submitting ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RadioPill({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={onChange}
      style={{
        flex: 1, padding: '10px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
        border: checked ? '2px solid #D4724A' : '1.5px solid #D9D9D9',
        background: checked ? '#FFF5F0' : '#fff',
        color: checked ? '#D4724A' : '#5A5A5A',
        fontWeight: checked ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}

// ---------- Styles ----------

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 7,
  border: '1.5px solid #D9D9D9', fontSize: 14, boxSizing: 'border-box',
  fontFamily: 'inherit', outline: 'none',
};

const fieldLabel = {
  display: 'block', fontSize: 13, fontWeight: 600,
  color: '#2D2D2D', marginBottom: 5,
};

const btnPrimary = {
  padding: '8px 16px', borderRadius: 8, border: 'none',
  background: '#D4724A', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const btnSecondary = {
  padding: '6px 12px', borderRadius: 7, border: '1px solid #D9D9D9',
  background: '#fff', color: '#2D2D2D', fontSize: 13, cursor: 'pointer',
};

const closeBtn = {
  background: 'none', border: 'none', fontSize: 24, cursor: 'pointer',
  color: '#8A8A8A', lineHeight: 1,
};

const errorBoxStyle = {
  padding: 16, borderRadius: 10, background: '#FDECEA',
  border: '1px solid rgba(192,57,43,0.3)', color: '#933',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  gap: 12, flexWrap: 'wrap',
};
